import { account } from '../config/appwrite';
import { AUTH_USERS } from '../config/authUsers';
import { findAppUserByEmail } from './appUsersService';

const SESSION_KEY = 'striitsmart:authSession:v1';

/** Only this account can manage login users (PINs, roles, access) on the Team page. */
export const PLATFORM_SUPER_ADMIN_EMAIL = 'joram.kalugira@sagemcom.com';

export const isPlatformSuperAdmin = (session) =>
  normalizeEmail(session?.email) === normalizeEmail(PLATFORM_SUPER_ADMIN_EMAIL);

export const ROLE_DRIVER = 'driver';
export const ROLE_POLES_LOT10 = 'poles_lot10';
export const ROLE_ADMIN_LOT10 = 'admin_lot10';
export const ROLE_FUEL = 'fuel';
export const LOT10_FILTER_VALUE = 'LOT 10 (Manyara)';
export const DRIVER_HOME = '/sagemcom/fleet-management?mode=drivers';
export const POLES_LOT10_HOME = '/sagemcom/logistics/delivery-tracking';
export const FUEL_HOME = '/sagemcom/fleet-management/fuel-report';
export const TOOLS_HOME = '/sagemcom/logistics/tools-inventory';

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

/** Appwrite password = PIN concatenated with itself (min 8 chars for 4-digit PINs). */
export const pinToPassword = (pin) => {
  const p = String(pin || '').trim();
  return `${p}${p}`;
};

export const readAuthSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.email) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const hasPolesLot10Access = (session) =>
  Boolean(session?.polesLot10) || session?.role === ROLE_POLES_LOT10;

export const isPolesLot10OnlyRole = (session) => session?.role === ROLE_POLES_LOT10;

export const isAdminLot10Role = (session) => session?.role === ROLE_ADMIN_LOT10;

/** Admin or poles role scoped to LOT 10 data only. */
export const isLot10ScopedSession = (session) =>
  isAdminLot10Role(session) || isPolesLot10OnlyRole(session);

/** When set, logistics pages lock their LOT filter to this value. */
export const getLockedLotFilter = (session) => {
  if (!session) return null;
  if (isAdminLot10Role(session)) return LOT10_FILTER_VALUE;
  const scope = String(session.lotScope || '');
  if (scope.includes('LOT 10')) return LOT10_FILTER_VALUE;
  return null;
};

export const isReadOnlySession = (session) =>
  Boolean(session?.readOnly) || isAdminLot10Role(session);

export const isDriverRole = (session) => session?.role === ROLE_DRIVER;

export const hasFuelAccess = (session) =>
  Boolean(session?.fuelAccess) || session?.role === ROLE_FUEL;

export const isFuelRole = (session) => session?.role === ROLE_FUEL;

export const hasToolsAccess = (session) => Boolean(session?.toolsAccess);

/** Home route after login / when a restricted role hits a blocked path. */
export const getPostLoginPath = (session, fallback = '/') => {
  if (session?.role === ROLE_DRIVER) return DRIVER_HOME;
  if (session?.role === ROLE_POLES_LOT10) return POLES_LOT10_HOME;
  if (session?.role === ROLE_FUEL) return FUEL_HOME;
  return fallback;
};

/** Whether the current pathname is allowed for this session role. */
export const isPathAllowedForSession = (session, pathname) => {
  if (!session?.email) return false;

  const fleetOk = pathname === '/sagemcom/fleet-management';
  const polesOk = pathname === '/sagemcom/logistics/delivery-tracking';
  const fuelOk = pathname === '/sagemcom/fleet-management/fuel-report';
  const toolsOk = pathname === TOOLS_HOME;

  if (session.role === ROLE_DRIVER) {
    if (fleetOk) return true;
    if (hasPolesLot10Access(session) && polesOk) return true;
    if (hasToolsAccess(session) && toolsOk) return true;
    return false;
  }

  if (session.role === ROLE_POLES_LOT10) {
    return polesOk;
  }

  if (session.role === ROLE_FUEL) {
    return fuelOk;
  }

  if (pathname === '/team/user-access' || pathname.startsWith('/team/user-access/')) {
    return isPlatformSuperAdmin(session);
  }

  return true;
};

export const writeAuthSession = (session) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* private mode / quota */
  }
};

export const clearAuthSession = () => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
};

/** Sync cache check — call refreshAuthSession() to verify Appwrite session. */
export const isAuthenticated = () => Boolean(readAuthSession()?.email);

const findLocalUser = (normalizedEmail) =>
  AUTH_USERS.find((u) => normalizeEmail(u.email) === normalizedEmail);

const buildSessionFromAccount = (user) => {
  const email = normalizeEmail(user.email);
  const prefs = user.prefs || {};
  const local = findLocalUser(email);

  const role = prefs.role || local?.role || 'admin';
  const driverKey = prefs.driverKey || local?.driverKey || null;
  const polesLot10 =
    Boolean(prefs.polesLot10) ||
    Boolean(local?.polesLot10) ||
    role === ROLE_POLES_LOT10;
  const fuelAccess =
    Boolean(prefs.fuelAccess) ||
    Boolean(local?.fuelAccess) ||
    role === ROLE_FUEL;
  const toolsAccess =
    Boolean(prefs.toolsAccess) ||
    Boolean(local?.toolsAccess);
  const lotScope = prefs.lotScope || local?.lotScope || (role === ROLE_ADMIN_LOT10 ? LOT10_FILTER_VALUE : '');
  const readOnly =
    Boolean(prefs.readOnly) ||
    Boolean(local?.readOnly) ||
    role === ROLE_ADMIN_LOT10;

  return {
    userId: user.$id,
    email,
    name: user.name || local?.name || email,
    role,
    driverKey: driverKey || null,
    polesLot10,
    fuelAccess,
    toolsAccess,
    lotScope: lotScope || null,
    readOnly,
    loggedInAt: new Date().toISOString()
  };
};

const applyAppUserToSession = (session, appUser) => {
  if (!appUser) return session;
  const role = appUser.role || session.role;
  const isLot10Admin = role === ROLE_ADMIN_LOT10;
  const hasAccessProfile = Boolean(appUser.hasAccessProfile);

  const polesLot10 = hasAccessProfile
    ? Boolean(appUser.polesLot10) || role === ROLE_POLES_LOT10
    : session.polesLot10;
  const fuelAccess = hasAccessProfile
    ? Boolean(appUser.fuelAccess) || role === ROLE_FUEL
    : session.fuelAccess;
  const toolsAccess = hasAccessProfile
    ? Boolean(appUser.toolsAccess)
    : session.toolsAccess;
  const readOnly = hasAccessProfile
    ? Boolean(appUser.readOnly) || isLot10Admin
    : isLot10Admin || Boolean(session.readOnly);
  const lotScope = hasAccessProfile
    ? appUser.lotScope || (isLot10Admin ? LOT10_FILTER_VALUE : session.lotScope)
    : isLot10Admin
      ? LOT10_FILTER_VALUE
      : session.lotScope;

  return {
    ...session,
    role,
    name: appUser.name || session.name,
    driverKey: appUser.driverKey || session.driverKey || null,
    polesLot10,
    fuelAccess,
    toolsAccess,
    readOnly,
    lotScope: lotScope || null,
    active: appUser.active !== false
  };
};

/**
 * Load current Appwrite account into sessionStorage cache.
 * Returns null when there is no valid Appwrite session.
 */
export const refreshAuthSession = async () => {
  try {
    const user = await account.get();
    let session = buildSessionFromAccount(user);

    try {
      const appUser = await findAppUserByEmail(session.email);
      if (appUser) session = applyAppUserToSession(session, appUser);
      if (appUser?.active === false) {
        clearAuthSession();
        try {
          await account.deleteSession('current');
        } catch {
          /* ignore */
        }
        return null;
      }
    } catch {
      /* AppUsers lookup is best-effort */
    }

    writeAuthSession(session);
    return session;
  } catch {
    clearAuthSession();
    return null;
  }
};

const mapLoginError = (err) => {
  const msg = String(err?.message || '');
  const code = err?.code || err?.type;
  if (
    code === 401 ||
    /invalid credentials|invalid `email` or `password`|user_invalid_credentials/i.test(msg)
  ) {
    return new Error('Invalid email or PIN. Please try again.');
  }
  if (/rate.?limit|too many/i.test(msg)) {
    return new Error('Too many login attempts. Please wait and try again.');
  }
  return new Error(msg || 'Login failed. Please try again.');
};

/**
 * Sign in with email + PIN via Appwrite Auth.
 * Frontend PIN is doubled to form the Appwrite password (PIN+PIN).
 */
export const loginWithEmailPin = async (email, pin) => {
  const normalizedEmail = normalizeEmail(email);
  const pinValue = String(pin || '').trim();

  if (!normalizedEmail) {
    throw new Error('Please enter your email address.');
  }
  if (!pinValue) {
    throw new Error('Please enter your PIN.');
  }

  const password = pinToPassword(pinValue);

  try {
    try {
      await account.deleteSession('current');
    } catch {
      /* no existing session */
    }

    await account.createEmailPasswordSession(normalizedEmail, password);
  } catch (err) {
    clearAuthSession();
    throw mapLoginError(err);
  }

  const session = await refreshAuthSession();
  if (!session) {
    throw new Error('Signed in but could not load your account. Please try again.');
  }

  if (session.active === false) {
    await logout();
    throw new Error('This account is disabled. Contact your administrator.');
  }

  return session;
};

export const logout = async () => {
  try {
    await account.deleteSession('current');
  } catch {
    /* already signed out */
  }
  clearAuthSession();
};
